/**
 * @提及纯逻辑单点（14.11；群聊专用，旧版 chat_screen @节同口径）。
 *
 * 口径（im_flutterv3 实读）：
 * - 触发：光标前一字符是 '@' 且其前是行首/空白/`,.:;!?([{` 之一才弹面板
 *   （shouldOpenMentionPanel）；输入栏 @ 按钮直接弹（不走该判定）。
 * - 插入：`@名称 `（尾随空格）；由输入 '@' 触发时替换那个 '@'。写进消息文本
 *   的名称用**公开名**（群名片优先，禁止用我的好友备注——消息发给全群）；
 *   面板展示名另算（group_note 服务端已并进 Nickname，members-api）。
 * - 状态↔文本同步：文本里删掉 `@名称` 即撤销该目标；删掉 `@全员` 即撤销全员
 *   （syncMentionState——旧版 _syncMentionStateWithText）。
 * - 发送：t=2 全员（g 恒空）/ 1 点名（g=uid 列表）/ 0 无；选了全员后再点名
 *   不清全员，发送时全员优先（旧版 _mentionsAll 优先同口径）。
 * - 排序：本地消息窗口从新到旧收集发言人（排除自己与 uid≤0），命中者按新鲜度
 *   排前，其余保持服务端页序（rankMembersByRecentSpeech）。
 * - 过滤：昵称/username/userNo 大小写不敏感 contains（普通成员视角后两者被
 *   服务端抹零，自然无命中）。
 */

import type { GroupMember } from '~/worker/contract';

export const MENTION_ALL_TEXT = '@全员';

/** @ 前合法的「词边界」字符（旧版 _shouldOpenMentionPicker 同表：空白或这些标点） */
const BOUNDARY_CHARS = ',.:;!?([{';

export interface MentionTarget {
  readonly uid: number;
  /** 插入消息文本的公开名（撤销判定用：文本里不再含 `@name` 即移除该目标） */
  readonly name: string;
}

export interface MentionState {
  readonly all: boolean;
  readonly targets: readonly MentionTarget[];
}

export const EMPTY_MENTION_STATE: MentionState = { all: false, targets: [] };

/** 输入 '@' 后是否该自动弹面板：光标前是 @ 且其前是行首/空白/边界标点 */
export function shouldOpenMentionPanel(text: string, caret: number): boolean {
  if (caret <= 0 || caret > text.length) return false;
  if (text[caret - 1] !== '@') return false;
  if (caret === 1) return true;
  const prev = text[caret - 2]!;
  return prev.trim() === '' || BOUNDARY_CHARS.includes(prev);
}

/**
 * 在光标处插入 `@名称 `；replaceTrailingAt=true 时替换光标前的那个 '@'
 * （由输入 '@' 触发的场景）。返回新文本与新光标位。
 */
export function insertMentionText(
  text: string,
  caret: number,
  name: string,
  replaceTrailingAt: boolean,
): { text: string; caret: number } {
  const at = Math.max(0, Math.min(caret, text.length));
  const start = replaceTrailingAt && at > 0 && text[at - 1] === '@' ? at - 1 : at;
  const insert = `@${name} `;
  return {
    text: text.slice(0, start) + insert + text.slice(at),
    caret: start + insert.length,
  };
}

/** 选中成员：追加目标（去重，后选的名字覆盖）；不碰 all（旧版同口径） */
export function addMentionTarget(state: MentionState, uid: number, name: string): MentionState {
  return {
    all: state.all,
    targets: [...state.targets.filter((t) => t.uid !== uid), { uid, name }],
  };
}

/** 选中 @全员：清空点名目标（旧版同口径） */
export function mentionAll(): MentionState {
  return { all: true, targets: [] };
}

/**
 * 文本变化后同步提及状态：`@名称` 已从文本消失的目标移除、`@全员` 消失则
 * 撤销全员。名字为空的目标保留（无从判定，发送时仍带上无害）。
 */
export function syncMentionState(state: MentionState, text: string): MentionState {
  const all = state.all && text.includes(MENTION_ALL_TEXT);
  const targets = state.targets.filter((t) => t.name === '' || text.includes(`@${t.name}`));
  if (all === state.all && targets.length === state.targets.length) return state;
  return { all, targets };
}

/** 发送用 t/g（buildTextEnvelope 的 mention 参数）：全员优先、g 仅点名时带 */
export function mentionEnvelopeFields(state: MentionState): {
  readonly type: number;
  readonly targets: readonly number[];
} {
  if (state.all) return { type: 2, targets: [] };
  if (state.targets.length > 0) return { type: 1, targets: state.targets.map((t) => t.uid) };
  return { type: 0, targets: [] };
}

/**
 * 从升序消息窗口收集「最近发言人」（新→旧去重；排除自己与 uid≤0）。
 * 调用方传 StoredMessage 最小形状即可。
 */
export function recentSpeakerUids(
  rowsAscending: readonly { readonly userId: number }[],
  myUid: number,
): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (let i = rowsAscending.length - 1; i >= 0; i--) {
    const uid = rowsAscending[i]!.userId;
    if (uid <= 0 || uid === myUid || seen.has(uid)) continue;
    seen.add(uid);
    out.push(uid);
  }
  return out;
}

/** 最近发言者排前（speakerUids 新→旧），其余保持原序（旧版排序同口径，稳定排序） */
export function rankMembersByRecentSpeech(
  members: readonly GroupMember[],
  speakerUids: readonly number[],
): GroupMember[] {
  if (speakerUids.length === 0) return [...members];
  const rank = new Map<number, number>();
  speakerUids.forEach((uid, i) => {
    if (!rank.has(uid)) rank.set(uid, i);
  });
  const ranked: { m: GroupMember; r: number }[] = [];
  const rest: GroupMember[] = [];
  for (const m of members) {
    const r = rank.get(m.userId);
    if (r !== undefined) ranked.push({ m, r });
    else rest.push(m);
  }
  ranked.sort((a, b) => a.r - b.r);
  return [...ranked.map((x) => x.m), ...rest];
}

/** 面板搜索过滤：昵称/username/userNo 大小写不敏感 contains（空查询原样返回） */
export function filterMembers(
  members: readonly GroupMember[],
  query: string,
): readonly GroupMember[] {
  const q = query.trim().toLowerCase();
  if (q === '') return members;
  return members.filter(
    (m) =>
      m.nickname.toLowerCase().includes(q) ||
      m.username.toLowerCase().includes(q) ||
      m.userNo.toLowerCase().includes(q),
  );
}
